import { Session, AbstractWalletPlugin, WalletPluginMetadata, PrivateKey } from "@wharfkit/session";
import { WhatifWalletPlugin } from "./whatifWallet";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, signInWithPopup, GoogleAuthProvider, signInWithCustomToken, signInWithCredential, signOut } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";


const WAX_CHAIN = {
    id: "1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4",
    url: "https://wax.greymass.com"
};

const STORAGE_KEY = "puzzl_google_wallet_session";
const COOKIE_KEY = "whatif_sso_session";

const CENTRAL_FIREBASE_CONFIG = {
    apiKey: "AIzaSyCAXf7cjUdCAo-Q2ra5eZ7mP_csFRmOh_o",
    authDomain: "puzzl-rewards.firebaseapp.com",
    projectId: "puzzl-rewards",
    storageBucket: "puzzl-rewards.firebasestorage.app",
    messagingSenderId: "1083602306526",
    appId: "1:1083602306526:web:21cd85f1a7bf039078c94f",
    measurementId: "G-8H2JS0S0BT"
};

function getCentralApp() {
    const existing = getApps().find(a => a.name === "whatif-wallet" || a.name === "[DEFAULT]");
    if (existing && existing.options.projectId === CENTRAL_FIREBASE_CONFIG.projectId) {
        return existing;
    }
    const named = getApps().find(a => a.name === "whatif-wallet");
    if (named) return named;
    return initializeApp(CENTRAL_FIREBASE_CONFIG, "whatif-wallet");
}

// Google sign-in finds the player's one WAX account (the same on every WHATIF site and app);
// their WHATIF Wallet passkey is what signs. The first time, the wallet moves their account to
// that passkey (or creates it): from then on only the player's passkey and backup code control
// it -- WHATIF never holds the key.
let walletOptions = {};

/**
 * Phone apps: the same options their WhatifWalletPlugin gets ({ returnUrl, openUrl }), so the
 * wallet opens in the phone's browser and answers through the app's link.
 */
export function setGoogleWalletOptions(options) {
    walletOptions = options || {};
}

function saveSessionToStorage(waxAccount) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ waxAccount, passkey: true }));
    } catch (e) {}

    // Security: Do NOT write private keys to document.cookie (broadcasting keys to all subdomains & HTTP requests).
    // Proactively clear any legacy SSO cookie from earlier sessions.
    try {
        if (typeof document !== 'undefined') {
            const host = window.location.hostname;
            const isWhatifDomain = host.endsWith("rewardsinwhatif.app");
            const domainClause = isWhatifDomain ? "; Domain=.rewardsinwhatif.app" : "";
            document.cookie = `${COOKIE_KEY}=; Path=/; Max-Age=0${domainClause}; SameSite=Lax; Secure`;
        }
    } catch (e) {}
}

/**
 * Creates and returns an active WharfKit Session for a Google wallet. It signs with the player's
 * passkey through WHATIF Wallet.
 */
export function createGoogleSession(waxAccount) {
    if (!waxAccount) return null;

    const session = new Session({
        chain: WAX_CHAIN,
        actor: waxAccount,
        permission: "active",
        walletPlugin: new WhatifWalletPlugin(walletOptions)
    });

    session.isGoogleWallet = true;
    saveSessionToStorage(waxAccount);

    return session;
}

// Play Store reviewer test account only: its key ships inside the app (handleReviewerLogin
// in App.js), so it signs locally. Real players' Google wallets never do.
class LocalKeyWalletPlugin extends AbstractWalletPlugin {
    constructor(privateKey) {
        super();
        this.id = "reviewer-local-key";
        this.metadata = new WalletPluginMetadata({ name: "Reviewer Test Account" });
        this.privateKey = PrivateKey.from(privateKey);
    }

    async login(context) {
        return {
            chain: context.chain.id,
            permissionLevel: { actor: context.actor, permission: "active" }
        };
    }

    async sign(resolved, context) {
        return {
            signatures: [this.privateKey.signDigest(resolved.transaction.signingDigest(context.chain.id))]
        };
    }
}

// Not saved to storage, so the reviewer logs in again after a reload. The demo account isn't on
// WAX, so a throwaway key is enough: nothing it signs can land.
export function createReviewerSession(waxAccount) {
    const session = new Session({
        chain: WAX_CHAIN,
        actor: waxAccount,
        permission: "active",
        walletPlugin: new LocalKeyWalletPlugin(PrivateKey.generate("K1"))
    });
    session.isGoogleWallet = true;
    return session;
}

/**
 * Restores a Google-embedded session from secure localStorage.
 */
export function restoreGoogleSession(expectedAccount = null) {
    try {
        let raw = null;
        if (typeof localStorage !== 'undefined') {
            raw = localStorage.getItem(STORAGE_KEY);
        }
        if (!raw) return null;
        const data = JSON.parse(raw);
        // Saved before passkeys, when the server signed: sign in with Google again to switch
        if (!data.waxAccount || !data.passkey) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }

        if (expectedAccount && data.waxAccount !== expectedAccount) {
            return null;
        }

        return createGoogleSession(data.waxAccount);
    } catch (e) {
        console.warn("Failed to restore Google wallet session:", e);
        return null;
    }
}

/**
 * Clears the stored Google wallet session from localStorage and root cookie on logout.
 */
export function clearGoogleSession() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}

    // End the Google sign-in on the central project too
    signOut(getAuth(getCentralApp())).catch(() => {});

    try {
        if (typeof document !== 'undefined') {
            const host = window.location.hostname;
            const isWhatifDomain = host.endsWith("rewardsinwhatif.app");
            const domainClause = isWhatifDomain ? "; Domain=.rewardsinwhatif.app" : "";
            document.cookie = `${COOKIE_KEY}=; Path=/; Max-Age=0${domainClause}; SameSite=Lax; Secure`;
        }
    } catch (e) {}
}

/**
 * After Google sign-in on the central project: finds the player's WAX account and, the first
 * time, has WHATIF Wallet move it to their passkey (or create it). Returns
 * { waxAccount, customToken, isNew }.
 */
export async function finishGoogleLogin() {
    const authGoogleWax = httpsCallable(getFunctions(getCentralApp(), "us-central1"), "puzzl_authGoogleWaxAccount");
    let res = (await authGoogleWax()).data;
    let isNew = res?.status === "new";
    if (res?.status === "switch" || res?.status === "new") {
        const setup = await new WhatifWalletPlugin(walletOptions).setup(res.setupTicket);
        // An account they already had and linked isn't new, and got no welcome gems
        if (setup?.imported) isNew = false;
        res = (await authGoogleWax()).data;
    }
    if (res?.status !== "ready" || !res.waxAccount || !res.customToken) {
        throw new Error(res?.message || "Couldn't finish setting up your WHATIF Wallet.");
    }
    return { waxAccount: res.waxAccount, customToken: res.customToken, isNew };
}

/**
 * One-click Google Sign-In: authenticates with Google, finds the player's WAX account (setting
 * up their WHATIF Wallet passkey the first time), and returns an active WharfKit session.
 */
export async function loginWithGoogle() {
    const app = getCentralApp();
    const auth = getAuth(app);

    let googleUser = null;

    if (Capacitor.isNativePlatform()) {
        try {
            let result;
            try {
                result = await FirebaseAuthentication.signInWithGoogle();
            } catch (nativeErr) {
                console.warn("[GoogleAuth] Credential Manager sign-in failed, attempting legacy fallback:", nativeErr);
                result = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: false });
            }
            const idToken = result.credential?.idToken;
            if (!idToken) throw new Error("No Google ID token returned from native sign-in.");
            const credential = GoogleAuthProvider.credential(idToken);
            const cred = await signInWithCredential(auth, credential);
            googleUser = cred.user;
        } catch (nativeOverallErr) {
            console.warn("[GoogleAuth] Native Google sign-in failed, falling back to popup sign-in:", nativeOverallErr);
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');
            const cred = await signInWithPopup(auth, provider);
            googleUser = cred?.user;
        }
    } else {
        const provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        const cred = await signInWithPopup(auth, provider);
        googleUser = cred?.user;
    }

    if (!googleUser) throw new Error("Google sign-in was cancelled.");

    const { waxAccount, customToken, isNew } = await finishGoogleLogin();

    // The site's own server accepts this token as proof of the sign-in that just happened, so
    // Google is all it takes: passkeys are for what goes on chain
    let connectProof = null;
    try {
        await signInWithCustomToken(auth, customToken);
        connectProof = { type: "google", idToken: await auth.currentUser.getIdToken() };
    } catch (e) {
        console.warn("Could not sign in with custom token on central auth:", e);
    }

    const session = createGoogleSession(waxAccount);

    return {
        session,
        waxAccount,
        customToken,
        isNew,
        connectProof
    };
}
